import { cva, type VariantProps } from 'class-variance-authority'
import { useState } from 'react'
import { cn } from '../lib/cn'

/** Maps the Figma `<Avatar>` set: 108 variants (size x shape x type x status). */
const avatar = cva(
  'relative inline-flex shrink-0 items-center justify-center overflow-hidden bg-slate-200 text-slate-600 font-medium select-none',
  {
    variants: {
      // The Figma set's own ramp: 24 / 32 / 40 / 48 / 56 / 64. `xl` and `xxl`
      // were 64 and 80 here, which made every card avatar the wrong size.
      size: {
        xs: 'size-6 text-2xs',
        sm: 'size-8 text-2xs',
        md: 'size-10 text-xs',
        lg: 'size-12 text-sm',
        xl: 'size-14 text-base',
        xxl: 'size-16 text-lg',
      },
      shape: { corner: 'rounded-md', rounded: 'rounded-lg', full: 'rounded-full' },
    },
    defaultVariants: { size: 'md', shape: 'full' },
  },
)

export type AvatarProps = VariantProps<typeof avatar> & {
  /** Full name; used for initials and as the accessible label. */
  name: string
  src?: string | undefined
  status?: 'online' | 'offline' | 'busy' | undefined
  className?: string
  /**
   * Set when the name is already visible next to the avatar. Without it a
   * screen reader announces the name twice ("Maria Magda Maria Magda").
   */
  decorative?: boolean | undefined
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

const statusColor = { online: 'bg-success', offline: 'bg-slate-400', busy: 'bg-warning' }

export function Avatar({
  name,
  src,
  size,
  shape,
  status,
  className,
  decorative = false,
}: AvatarProps) {
  const [failed, setFailed] = useState(false)
  // A signed photo URL arrives a moment after the row does, so the picture
  // lands on top of the initials. Fading it in means the swap reads as the
  // photo arriving rather than as the row flickering.
  const [loaded, setLoaded] = useState(false)
  const showImage = Boolean(src) && !failed
  return (
    <span
      className={cn('relative inline-flex', className)}
      {...(decorative ? { 'aria-hidden': true } : {})}
    >
      <span className={cn(avatar({ size, shape }), 'transition-colors duration-[--duration-base]')}>
        {showImage ? (
          <img
            src={src}
            alt={decorative ? '' : name}
            className={cn(
              'size-full object-cover transition-opacity duration-[--duration-base] ease-[--ease-out-soft]',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
            // A cached image can finish loading before React attaches the
            // handler below, which would leave it at opacity 0 for ever. The
            // ref catches that case on mount.
            ref={(node) => {
              if (node?.complete && node.naturalWidth > 0) setLoaded(true)
            }}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            loading="lazy"
          />
        ) : (
          <span aria-hidden="true">{initials(name)}</span>
        )}
      </span>
      {!showImage && !decorative ? <span className="sr-only">{name}</span> : null}
      {status ? (
        <>
          <span
            className={cn(
              'absolute right-0 bottom-0 size-2.5 rounded-full border-2 border-white',
              statusColor[status],
            )}
            aria-hidden="true"
          />
          {decorative ? null : <span className="sr-only">{status}</span>}
        </>
      ) : null}
    </span>
  )
}
